import React, { useEffect, useState } from 'react';
import axios from 'axios';
import FilterDrawer from '../../../shared/FilterDrawer';
import ConfirmDialog from '../../../shared/ConfirmDialog';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import RequiredMark from '../../../shared/RequiredMark';
import DateInput from '../../../shared/DateInput';
import NumberInput from '../../../shared/NumberInput';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { formatDate, formatDateTime, todayInTenant } from '../../../shared/tenantClock';
import { addressLines, postcodeWarning } from '../../../shared/addressFormat';
import { AddressFieldWarning, PostcodeCheckPill } from '../../../shared/AddressFields';
import { businessNumberLabel } from '../../../shared/businessNumber';
import '../../../shared/StyledInput.css';
import CommercialTermsPrompt, { hasCommercialTerms } from './CommercialTermsPrompt';
import { StatusPill } from './AccountApplicationsCard';

// One Trading or Credit Account Application (issue #290): what the
// applicant entered, with each pre-filled field they changed shown beside
// what Optim held, and the signature. A signed application is approved or
// declined here; a declined one can be sent the "not at this time" email
// after staff have phoned the applicant, never automatically.
//
// Approving a Prospect's Trading Account Application makes it a Customer,
// so it asks for the Tier (and Price Group) the same way Convert to
// Customer does (CommercialTermsPrompt). Approving a Credit Account
// Application asks for the Payment Term and Credit Limit. Declining a
// Prospect's application closes it as Disqualified, so it asks for a Close
// Reason (and a revisit date when the reason has a revisit period).

const SECTION_STYLE = { marginBottom: 18 };
const HEADING_STYLE = { fontSize: 12, fontWeight: 600, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.4, margin: '0 0 6px' };
const ROW_STYLE = { display: 'grid', gridTemplateColumns: '150px 1fr', gap: 8, fontSize: 13, padding: '3px 0' };
const LABEL_STYLE = { color: '#5a6b85' };
const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };
const CHANGED_STYLE = { fontSize: 12, color: '#92400e' };

// 'YYYY-MM-DD' `months` months after `ymd` (same as CloseProspectDialog).
function addMonths(ymd, months) {
  const [y, m, d] = ymd.split('-').map(Number);
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return target.toISOString().slice(0, 10);
}

function Row({ label, value, was }) {
  return (
    <div style={ROW_STYLE}>
      <span style={LABEL_STYLE}>{label}</span>
      <span style={{ wordBreak: 'break-word' }}>
        {value || '—'}
        {was !== undefined && <div style={CHANGED_STYLE}>Changed: Optim held {was || '(blank)'}</div>}
      </span>
    </div>
  );
}

function Person({ title, person, changed = {} }) {
  if (!person) return null;
  return (
    <div style={SECTION_STYLE}>
      <h4 style={HEADING_STYLE}>{title}</h4>
      <Row label={title === 'Primary Contact' ? 'Job Title / Role' : 'Role'} value={person.role} />
      <Row label="First Name" value={person.first_name} was={changed.first_name} />
      <Row label="Family Name" value={person.family_name} was={changed.family_name} />
      <Row label="Contact Phone" value={person.phone} was={changed.phone} />
      <Row label="Contact Email" value={person.email} was={changed.email} />
    </div>
  );
}

// The Registered Address in its country's layout, an off-pattern Postcode
// flagged (#395), and Australia Post's check of an Australian one (#158).
function RegisteredAddress({ address }) {
  const warning = postcodeWarning(address);
  return (
    <div style={ROW_STYLE}>
      <span style={LABEL_STYLE}>Registered Address</span>
      <span style={{ wordBreak: 'break-word' }}>
        {addressLines(address).map((line) => <div key={line}>{line}</div>)}
        {address.postcode_check && <div style={{ marginTop: 4 }}><PostcodeCheckPill status={address.postcode_check} /></div>}
        <AddressFieldWarning message={warning} />
      </span>
    </div>
  );
}

// Changed fields keyed by section, then field:
// { business: { business_number: 'held' } }.
const DIFF_KEYS = {
  'Business Name': ['business', 'name'],
  Country: ['business', 'country'],
  'Business Number': ['business', 'business_number'],
  'Primary Contact first name': ['primary_contact', 'first_name'],
  'Primary Contact family name': ['primary_contact', 'family_name'],
  'Primary Contact email': ['primary_contact', 'email'],
  'Primary Contact phone': ['primary_contact', 'phone'],
  'First Name': ['director', 'first_name'],
  'Family Name': ['director', 'family_name'],
  'Contact Phone': ['director', 'phone'],
  'Contact Email': ['director', 'email'],
};
function changedBySection(differences) {
  const out = {};
  for (const d of differences || []) {
    const [section, field] = DIFF_KEYS[d.label] || [];
    if (!section) continue;
    out[section] = { ...(out[section] || {}), [field]: d.held };
  }
  return out;
}

function Answers({ app }) {
  const a = app.answers;
  if (!a) return <div style={{ fontSize: 13, color: '#999' }}>Not filled in yet.</div>;
  const changed = changedBySection(app.differences);
  const address = a.business.address;
  return (
    <>
      <div style={SECTION_STYLE}>
        <h4 style={HEADING_STYLE}>{app.kind === 'credit' ? 'Customer' : 'The Customer'}</h4>
        <Row label="Business Name" value={a.business.name} was={changed.business?.name} />
        <Row label="Country" value={a.business.country} was={changed.business?.country} />
        <Row label={`Business ${businessNumberLabel(a.business.business_number_type)}`} value={a.business.business_number} was={changed.business?.business_number} />
        {a.business.entity_type && <Row label="Entity Type" value={a.business.entity_type} />}
        {address && <RegisteredAddress address={address} />}
      </div>
      {a.about && (
        <div style={SECTION_STYLE}>
          <h4 style={HEADING_STYLE}>About the Business</h4>
          <Row label="Website" value={a.about.website} />
          {a.about.linkedin && <Row label="LinkedIn" value={a.about.linkedin} />}
          {a.about.instagram && <Row label="Instagram" value={a.about.instagram} />}
          {a.about.facebook && <Row label="Facebook" value={a.about.facebook} />}
          <Row label="Physical shop" value={a.about.physical_shop} />
          <Row label="Brands of interest" value={Array.isArray(a.about.brands) ? a.about.brands.join(', ') : a.about.brands} />
          {a.about.other_products && <Row label="Other products" value={a.about.other_products} />}
          <Row label="Expected yearly purchasing" value={a.about.yearly_purchasing} />
        </div>
      )}
      <Person title="Primary Contact" person={a.primary_contact} changed={changed.primary_contact} />
      <Person title={a.directors.length > 1 ? 'Director / Proprietor (signer)' : 'Director / Proprietor'} person={a.directors[0]} changed={changed.director} />
      {a.directors[1] && <Person title="Second Director / Proprietor" person={a.directors[1]} />}
    </>
  );
}

// Payment Term and Credit Limit for an approved Credit Account Application.
function ApproveCreditDialog({ app, entity, onCancel, onConfirm }) {
  const [terms, setTerms] = useState([]);
  const [termId, setTermId] = useState(entity?.payment_term_id ? String(entity.payment_term_id) : '');
  const [limit, setLimit] = useState(entity?.credit_limit != null ? String(entity.credit_limit) : '');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get('/api/payment-terms', { params: { status: 'active' } })
      .then((res) => setTerms(res.data.data || []))
      .catch(() => setTerms([]));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await onConfirm({ payment_term_id: Number(termId), credit_limit: limit });
    } catch (err) {
      setSaving(false);
    }
  };
  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => { if (!next && !saving) onCancel(); }}
      title={`Approve ${app.label}`}
      description="The Customer sees these on the portal's Terms page."
      footer={(
        <div style={{ display: 'flex', gap: 8 }}>
          <CancelButton onClick={onCancel} disabled={saving} />
          <SaveButton type="button" onClick={save} loading={saving} disabled={!termId || limit === ''}>{saving ? 'Approving...' : 'Approve'}</SaveButton>
        </div>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={FIELD_LABEL_STYLE}>Payment Term<RequiredMark /></label><br />
          <select className="styled-input" value={termId} onChange={(e) => setTermId(e.target.value)} style={FIELD_INPUT_STYLE}>
            <option value="" disabled>Select...</option>
            {terms.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label style={FIELD_LABEL_STYLE}>Credit Limit<RequiredMark /></label><br />
          <NumberInput value={limit} onChange={(e) => setLimit(e.target.value)} decimals={2} min="0" style={FIELD_INPUT_STYLE} />
        </div>
      </div>
    </ConfirmDialog>
  );
}

function ConfirmApproveDialog({ app, entity, becomesCustomer, onCancel, onConfirm }) {
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await onConfirm({});
    } catch (err) {
      setSaving(false);
    }
  };
  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => { if (!next && !saving) onCancel(); }}
      title={`Approve ${app.label}`}
      description={becomesCustomer
        ? `${entity.name} becomes a Customer at Active, with the application's business details and people added. Its Primary Contact is invited to the portal.`
        : "Records the signature, and adds the application's business details and people to this record."}
      footer={(
        <div style={{ display: 'flex', gap: 8 }}>
          <CancelButton onClick={onCancel} disabled={saving} />
          <SaveButton type="button" onClick={save} loading={saving}>{saving ? 'Approving...' : 'Approve'}</SaveButton>
        </div>
      )}
    />
  );
}

// Decline: a Prospect's Trading Account Application closes it as
// Disqualified, so it needs a Close Reason; anything else just confirms.
function DeclineDialog({ app, closesProspect, onCancel, onConfirm }) {
  const [stageId, setStageId] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [reasonId, setReasonId] = useState('');
  const [revisitDate, setRevisitDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!closesProspect) return;
    axios.get('/api/relationship-types', { params: { status: 'active' } })
      .then((res) => {
        const prospectType = (res.data.data || []).find((t) => t.system_key === 'prospect');
        const stage = (prospectType?.stages || []).find((s) => s.system_key === 'disqualified');
        if (!stage) return;
        setStageId(stage.id);
        return axios.get('/api/close-reasons', { params: { status: 'active', stage_id: stage.id } }).then((r) => setReasons(r.data.data || []));
      })
      .catch(() => setReasons([]));
  }, [closesProspect]);

  const reason = reasons.find((r) => String(r.id) === String(reasonId));
  const pickReason = (id) => {
    setReasonId(id);
    const picked = reasons.find((r) => String(r.id) === String(id));
    setRevisitDate(picked?.revisit_months ? addMonths(todayInTenant(), picked.revisit_months) : '');
  };

  const save = async () => {
    setSaving(true);
    try {
      await onConfirm(closesProspect ? { close_reason_id: Number(reasonId), revisit_date: revisitDate || null } : {});
    } catch (err) {
      setSaving(false);
    }
  };
  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => { if (!next && !saving) onCancel(); }}
      title={`Decline ${app.label}`}
      description={closesProspect
        ? 'The Prospect is closed as Disqualified. Nothing is sent to the applicant.'
        : 'Nothing is sent to the applicant, and nothing on the record changes.'}
      footer={(
        <div style={{ display: 'flex', gap: 8 }}>
          <CancelButton onClick={onCancel} disabled={saving} />
          <SaveButton type="button" onClick={save} loading={saving} disabled={closesProspect && (!stageId || !reasonId)}>{saving ? 'Declining...' : 'Decline'}</SaveButton>
        </div>
      )}
    >
      {closesProspect && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={FIELD_LABEL_STYLE}>Close Reason<RequiredMark /></label><br />
            <select className="styled-input" value={reasonId} onChange={(e) => pickReason(e.target.value)} style={FIELD_INPUT_STYLE}>
              <option value="" disabled>Select...</option>
              {reasons.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          {reason?.revisit_months ? (
            <div>
              <label style={FIELD_LABEL_STYLE}>Revisit on</label><br />
              <DateInput value={revisitDate} onChange={(e) => setRevisitDate(e.target.value)} style={{ width: 180 }} />
            </div>
          ) : null}
        </div>
      )}
    </ConfirmDialog>
  );
}

function NotNowDialog({ app, onCancel, onConfirm }) {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await onConfirm({ call_notes: notes });
    } catch (err) {
      setSaving(false);
    }
  };
  const credit = app.kind === 'credit';
  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => { if (!next && !saving) onCancel(); }}
      title='Send "not at this time" email'
      description={credit
        ? "Thanks them for applying and says credit can't be offered at this time; their Trading Account carries on as it is."
        : "Thanks them for applying and says a Trading Account can't be opened at this time."}
      footer={(
        <div style={{ display: 'flex', gap: 8 }}>
          <CancelButton onClick={onCancel} disabled={saving} />
          <SaveButton type="button" onClick={save} loading={saving}>{saving ? 'Sending...' : 'Send email'}</SaveButton>
        </div>
      )}
    >
      <label style={FIELD_LABEL_STYLE} htmlFor="not-now-notes">Call notes (optional)</label>
      <textarea id="not-now-notes" className="styled-input" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} style={{ ...FIELD_INPUT_STYLE, resize: 'vertical' }} />
      <div style={{ fontSize: 12, color: '#5a6b85', marginTop: 4 }}>Notes here log your call as an Activity on this record.</div>
    </ConfirmDialog>
  );
}

function AccountApplicationDrawer({ entityId, entity, applicationId, onClose, onChanged }) {
  const { canUpdate } = useScreenAccess();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [app, setApp] = useState(null);
  const [dialog, setDialog] = useState(null); // 'approve' | 'decline' | 'not-now'

  const load = () => axios.get(`/api/commercial-entities/${entityId}/applications/${applicationId}`)
    .then((res) => setApp(res.data.data))
    .catch((err) => { showError(err, 'Could not load the application'); onClose(); });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, applicationId]);

  const act = async (path, body, fallback) => {
    try {
      const res = await axios.post(`/api/commercial-entities/${entityId}/applications/${applicationId}/${path}`, body);
      dismissError();
      setDialog(null);
      onChanged(res.data.data, res.data.message);
      await load();
    } catch (err) {
      showError(err, fallback);
      throw err;
    }
  };
  const approve = (terms) => act('approve', terms, 'Approve failed');

  const standing = app?.business_standing;
  const prospectTrading = app?.kind === 'trading' && standing?.kind === 'prospect';

  const footer = app && canUpdate && (
    <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
      {app.status === 'submitted' && (
        <>
          <CancelButton onClick={() => setDialog('decline')}>Decline</CancelButton>
          <SaveButton type="button" onClick={() => setDialog('approve')}>Approve</SaveButton>
        </>
      )}
      {app.status === 'declined' && !app.not_now_sent_at && (
        <SaveButton type="button" onClick={() => setDialog('not-now')}>Send &quot;not at this time&quot; email</SaveButton>
      )}
    </div>
  );

  return (
    <>
      <FilterDrawer
        open
        onOpenChange={(next) => { if (!next) onClose(); }}
        title={app ? app.label : 'Application'}
        description={app ? `Sent to ${app.email} on ${formatDate(app.sent_at)}` : 'Loading...'}
        width={520}
        footer={footer}
      >
        {app && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <StatusPill status={app.status} />
              {app.status === 'sent' && <span style={{ fontSize: 12, color: '#5a6b85' }}>Link works until {formatDate(app.expires_at)}</span>}
              {app.not_now_sent_at && <span style={{ fontSize: 12, color: '#5a6b85' }}>&quot;Not at this time&quot; email sent {formatDate(app.not_now_sent_at)}</span>}
            </div>
            {app.differences?.length > 0 && (
              <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px', marginBottom: 14 }}>
                The applicant changed {app.differences.map((d) => d.label).join(', ')} from what Optim held.
              </div>
            )}
            <Answers app={app} />
            {app.signature && (
              <div style={SECTION_STYLE}>
                <h4 style={HEADING_STYLE}>Signature</h4>
                <Row label="Signed by" value={`${app.signature.name}${app.signature.role ? ` (${app.signature.role})` : ''}`} />
                <Row label="Signed" value={formatDateTime(app.signature.signed_at)} />
                <Row label="Agreed to" value={`${app.kind === 'credit' ? 'Credit Terms' : 'Terms & Conditions of Trade'} version ${app.terms_version}`} />
              </div>
            )}
          </>
        )}
      </FilterDrawer>
      {dialog === 'approve' && app && (
        prospectTrading && !hasCommercialTerms(entity) ? (
          <CommercialTermsPrompt
            entity={entity}
            stageName="Active"
            title={`Approve ${app.label}`}
            description={`${entity.name} becomes a Customer at Active, which needs commercial terms. Its Primary Contact is invited to the portal.`}
            confirmLabel="Approve"
            savingLabel="Approving..."
            onCancel={() => setDialog(null)}
            onConfirm={approve}
          />
        ) : app.kind === 'credit' ? (
          <ApproveCreditDialog app={app} entity={entity} onCancel={() => setDialog(null)} onConfirm={approve} />
        ) : (
          <ConfirmApproveDialog app={app} entity={entity} becomesCustomer={prospectTrading} onCancel={() => setDialog(null)} onConfirm={approve} />
        )
      )}
      {dialog === 'decline' && app && (
        <DeclineDialog app={app} closesProspect={prospectTrading} onCancel={() => setDialog(null)} onConfirm={(body) => act('decline', body, 'Decline failed')} />
      )}
      {dialog === 'not-now' && app && (
        <NotNowDialog app={app} onCancel={() => setDialog(null)} onConfirm={(body) => act('not-at-this-time', body, 'Email not sent')} />
      )}
    </>
  );
}

export default AccountApplicationDrawer;
